import React, { useMemo, useState } from "react";
import {
  Breadcrumbs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  makeStyles,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  TextField,
  Button,
  Grid,
  Tooltip,
  Input,
  Box,
  FormHelperText,
} from "@material-ui/core";
import { Link as RouterLink } from "react-router-dom";

import NavigateNextIcon from "@material-ui/icons/NavigateNext";

import { Alert } from "@material-ui/lab";

import moment from "moment";

import ExportIcon from "@material-ui/icons/SaveAlt";

import useAdminQueryWTMScansByTrackerDomains, {
  ScanByTrackerDomains,
} from "./queries/useAdminQueryWTMScansByTrackerDomains";
import Navigation from "../Navigation";
import useWTMURLs from "./useWTMURLs";

import { theme } from "../theme";

import DownloadReportButton from "./DownloadReportButton";
import ColumnLabelWithTooltip from "./ColumnLabelWithTooltip";
import FormLabelWithTooltip from "./WebTrackerManagerPage/FormLabelWithTooltip";

const MAX_NUM_DAYS_TO_QUERY = 31;

const CSV_HEADER_ROW = [
  "Report ID",
  "Site Domain",
  "Matched Tracker Domains",
  "Internal?",
  "Is Complete?",
  "Started At",
];

function renderCSV(rows: ScanByTrackerDomains[]): string {
  const csvRows = rows.map((r) => {
    return [
      r.id,
      r.domain,
      `"${r.trackerDomains.sort().join(", ")}"`,
      JSON.stringify(r.internal),
      JSON.stringify(r.isComplete),
      r.startedAt.toJSON(),
    ];
  });

  const rowsSorted = csvRows.sort((a, b) => {
    const domain = a[1].localeCompare(b[1]);
    const startedAt = a[5].localeCompare(b[5]);
    const id = a[0].localeCompare(b[0]);

    return domain + startedAt + id;
  });

  return [CSV_HEADER_ROW, ...rowsSorted].map((row) => row.join(",")).join("\n");
}

function handleExportAsCSV(
  searchString: string,
  from: Date,
  to: Date,
  rows: ScanByTrackerDomains[]
): void {
  const csvRows = renderCSV(rows);
  const fromDate = from.toJSON().slice(0, 10);
  const toDate = to.toJSON().slice(0, 10);
  const fileName = `scan_results_${searchString}_${fromDate}_to_${toDate}.csv`;

  /**
   * Credit to the following StackOverflow post for the following code snippet!
   *
   * @see https://stackoverflow.com/questions/14964035/how-to-export-javascript-array-info-to-csv-on-client-side
   */
  const encoded = encodeURI(`data:text/csv;charset=utf-8,${csvRows}`);
  const link = document.createElement("a");
  link.setAttribute("href", encoded);
  link.setAttribute("download", fileName);
  link.style.display = "none";
  document.body.appendChild(link);

  link.click();

  document.body.removeChild(link);
}

type ResultsTableProps = {
  searchString: string;
  trackerDomains: string[];
  fromDate: Date;
  toDate: Date;
  onSuccess: () => void;
};

function ResultsTable({
  searchString,
  trackerDomains,
  fromDate,
  toDate,
  onSuccess,
}: ResultsTableProps) {
  const { data, isLoading } = useAdminQueryWTMScansByTrackerDomains(
    trackerDomains,
    fromDate,
    toDate,
    onSuccess
  );

  const rows = data || [];

  if (isLoading) {
    return (
      <TableContainer>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell colSpan={5}>Loading...</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>
              <ColumnLabelWithTooltip
                label="Site Domain"
                tooltip="Domain of the scanned site"
              />
            </TableCell>
            <TableCell>Started At</TableCell>
            <TableCell>
              <ColumnLabelWithTooltip
                label="Complete?"
                tooltip="Whether the WTM scan completed successfully"
              />
            </TableCell>
            <TableCell>
              <ColumnLabelWithTooltip
                label="Internal?"
                tooltip="Whether the WTM scan was internal. Internal scans those generated on an ad hoc basis by internal Freshpaint employees."
              />
            </TableCell>
            <TableCell style={{ textAlign: "right" }}>
              {rows.length > 0 && (
                <Tooltip title="Export report as CSV file" arrow>
                  <Button
                    color="primary"
                    variant="contained"
                    startIcon={<ExportIcon />}
                    onClick={() =>
                      handleExportAsCSV(
                        searchString.replaceAll("*", "STAR"),
                        fromDate,
                        toDate,
                        rows
                      )
                    }
                  >
                    Export CSV
                  </Button>
                </Tooltip>
              )}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>No results found.</TableCell>
            </TableRow>
          )}
          {rows.map((row) => (
            <TableRow key={`wtm-scans-by-tracker-names-row-${row.id}`}>
              <TableCell>
                <Tooltip title={`Report ID: ${row.id}`}>
                  <span>{row.domain}</span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Tooltip title={row.startedAt.toJSON()}>
                  <span>{moment(row.startedAt).fromNow().toString()}</span>
                </Tooltip>
              </TableCell>
              <TableCell>{row.isComplete.toString()}</TableCell>
              <TableCell>{row.internal.toString()}</TableCell>
              <TableCell>
                <DownloadReportButton reportID={row.id} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const useStyles = makeStyles((theme) => ({
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  card: {
    minHeight: 500,
    position: "relative",
    width: "100%",
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  cardHeader: {
    padding: "1em",
  },
  cardNav: {
    padding: "1em",
  },
  formLabel: {
    fontWeight: 500,
    marginBottom: theme.spacing(1),
  },
  loader: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translateX(-50%) translateY(-50%)",
  },
  navigation: {
    paddingTop: 12,
  },
  pageTitle: {
    paddingTop: 12,
  },
}));

function AdminQueryWTMScansByTrackerNamesPage() {
  const classes = useStyles();
  const wtmURLs = useWTMURLs();
  const [trackerDomain, setTrackerDomain] = useState<string>("");
  const [trackerDomainDisplay, setTrackerDomainDisplay] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const [fromDateDisplay, setFromDateDisplay] = useState<Date>(
    moment(new Date()).subtract(MAX_NUM_DAYS_TO_QUERY, "days").toDate()
  );
  const [toDateDisplay, setToDateDisplay] = useState<Date>(new Date());

  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  const dateRangeError = useMemo(() => {
    const daysBetweenDates = moment(toDateDisplay).diff(
      fromDateDisplay,
      "days"
    );

    if (fromDateDisplay >= toDateDisplay) {
      return "From date must be before to date.";
    }

    if (daysBetweenDates > MAX_NUM_DAYS_TO_QUERY) {
      return `Date range of search cannot exceed ${MAX_NUM_DAYS_TO_QUERY} days.`;
    }

    return "";
  }, [fromDateDisplay, toDateDisplay]);

  const searchStringError = useMemo(() => {
    if (trackerDomainDisplay.trim() === "") {
      return "Please enter a tracker domain to search for, ie. googletagmanager.com.";
    }

    try {
      const urlToParse = trackerDomainDisplay.startsWith("*")
        ? `www${trackerDomainDisplay.slice(1)}`
        : trackerDomainDisplay;

      const url = new URL(`https://${urlToParse}`);
      if (
        urlToParse !== url.hostname ||
        trackerDomainDisplay.indexOf(".") === -1
      ) {
        return "Please enter a valid domain without a path, query, or hash, ie. googletagmanager.com.";
      }
    } catch (_e) {
      return "Please enter a valid domain, ie. googletagmanager.com.";
    }

    return "";
  }, [trackerDomainDisplay]);

  const onSearchChange = (searchString: string) => {
    setTrackerDomainDisplay(searchString);
  };

  const onSearch = () => {
    if (loading || dateRangeError !== "" || searchStringError !== "") {
      return;
    }

    setTrackerDomain(trackerDomainDisplay);
    setFromDate(fromDateDisplay);
    setToDate(toDateDisplay);
  };

  return (
    <Navigation>
      <div className={classes.pageTitle}>
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          aria-label="breadcrumb"
          className={classes.breadcrumb}
        >
          <RouterLink color="inherit" to={wtmURLs.trackersOverview}>
            <Typography variant="overline">Web Tracker Manager</Typography>
          </RouterLink>
          <Typography variant="overline" gutterBottom>
            Reports by Tracker Domains
          </Typography>
        </Breadcrumbs>
      </div>

      <Card className={classes.card}>
        <CardHeader
          className={classes.cardHeader}
          title="Query WTM Scans by Tracker Domains"
        />

        <Divider />

        <CardContent className={classes.cardContent}>
          <Box style={{ padding: "1em 1em 0 1em" }}>
            <Alert
              severity="error"
              style={{
                border: `1px solid ${theme.palette.grey["300"]}`,
                marginBottom: theme.spacing(2),
              }}
            >
              Querying for WTM reports by tracker domains can take 5-15 seconds.
              Please use this tool carefully to avoid degrading production
              database performance.
            </Alert>
          </Box>

          <Grid
            container
            spacing={0}
            style={{ padding: "0 1em", marginBottom: "1em" }}
          >
            <Grid item container xs={12} spacing={2}>
              <Grid item xs={2}>
                <FormControl fullWidth size="small">
                  <FormLabelWithTooltip
                    label="From Date"
                    tooltip="The start date to search from. 31 days is the max time range supported."
                  />
                  <Input
                    fullWidth
                    error={dateRangeError !== ""}
                    type="date"
                    value={fromDateDisplay.toJSON().slice(0, 10)}
                    onChange={(e) => {
                      setFromDate(null);
                      setFromDateDisplay(new Date(e.target.value));
                    }}
                    style={{ marginTop: theme.spacing(1) }}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={2}>
                <FormControl fullWidth size="small">
                  <FormLabelWithTooltip
                    label="To Date"
                    tooltip="The end date to search to (not inclusive). 31 days is the max time range supported."
                  />
                  <Input
                    fullWidth
                    error={dateRangeError !== ""}
                    type="date"
                    value={toDateDisplay.toJSON().slice(0, 10)}
                    onChange={(e) => {
                      setToDate(null);
                      setToDateDisplay(new Date(e.target.value));
                    }}
                    style={{ marginTop: theme.spacing(1) }}
                  />
                </FormControl>
              </Grid>

              <Grid item xs={8}>
                <FormControl fullWidth size="medium">
                  <FormLabelWithTooltip
                    label="Tracker Domain"
                    tooltip="The tracker domain to search for. This should be the domain of the tracker, ie. googletagmanager.com."
                  />

                  <Grid container spacing={1}>
                    <Grid item xs={9}>
                      <TextField
                        autoFocus
                        fullWidth
                        disabled={loading}
                        error={searchStringError !== ""}
                        placeholder="Tracker Domain, ie. googletagmanager.com"
                        size="small"
                        type="url"
                        value={trackerDomainDisplay}
                        variant="outlined"
                        onChange={(e) => onSearchChange(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key !== "Enter") {
                            return;
                          }

                          onSearch();
                        }}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      <Button
                        color="primary"
                        disabled={
                          loading ||
                          trackerDomainDisplay.trim() === "" ||
                          dateRangeError !== "" ||
                          searchStringError !== ""
                        }
                        size="medium"
                        variant="contained"
                        onClick={onSearch}
                      >
                        Search
                      </Button>
                    </Grid>
                  </Grid>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container item xs={12}>
              <Grid item xs={4}>
                {dateRangeError !== "" && (
                  <FormHelperText error>{dateRangeError}</FormHelperText>
                )}
              </Grid>

              <Grid item xs={8}>
                {searchStringError !== "" && (
                  <FormHelperText error>{searchStringError}</FormHelperText>
                )}
              </Grid>
            </Grid>
          </Grid>

          {trackerDomain !== "" && fromDate !== null && toDate !== null && (
            <ResultsTable
              searchString={trackerDomainDisplay}
              trackerDomains={[trackerDomain]}
              fromDate={fromDate}
              toDate={toDate}
              onSuccess={() => setLoading(false)}
            />
          )}
        </CardContent>
      </Card>
    </Navigation>
  );
}

export default AdminQueryWTMScansByTrackerNamesPage;
